import type { PublicCertificateRequest } from "@homesite/api-contract";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useApiClient } from "../lib/ApiClientContext";

export interface UseGenerateCertificateReturn {
	generate: (input: PublicCertificateRequest) => Promise<Blob>;
	/**
	 * Generates the certificate and triggers a browser download immediately.
	 * Filename derives from kind + target. Returns the Blob.
	 */
	generateAndDownload: (input: PublicCertificateRequest) => Promise<Blob>;
	isPending: boolean;
	isError: boolean;
	error: Error | null;
	/**
	 * Object URL for the generated PDF blob. Non-null after a successful generate call.
	 * Revoke via URL.revokeObjectURL when unmounting if needed.
	 */
	downloadUrl: string | null;
	reset: () => void;
}

/**
 * Mutation that downloads a PDF audit certificate.
 *
 * NOTE: This is the ONE place in @homesite/hooks that calls fetch directly.
 * The ts-rest contract models the certificate endpoint as returning z.unknown()
 * because the server streams application/pdf binary — ts-rest cannot model
 * binary streams. We therefore bypass the typed client and call the download
 * endpoint directly via fetch.
 *
 * The binary endpoint is at:
 *   POST ${baseUrl}/api/public/reports/certificate/download
 */
export function useGenerateCertificate(
	apiBaseUrl: string,
): UseGenerateCertificateReturn {
	// Call useApiClient to enforce the provider requirement — result unused
	// since the binary endpoint bypasses the typed ts-rest client.
	useApiClient();
	const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: async (input: PublicCertificateRequest): Promise<Blob> => {
			const response = await fetch(
				`${apiBaseUrl}/api/public/reports/certificate/download`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(input),
				},
			);

			if (!response.ok) {
				throw new Error(
					`Certificate generation failed: ${response.status} ${response.statusText}`,
				);
			}

			const blob = await response.blob();

			// Revoke any previous object URL to avoid memory leaks
			setDownloadUrl((prev) => {
				if (prev) URL.revokeObjectURL(prev);
				return URL.createObjectURL(blob);
			});

			return blob;
		},
	});

	async function generateAndDownload(
		input: PublicCertificateRequest,
	): Promise<Blob> {
		const blob = await mutation.mutateAsync(input);
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${input.kind}-${input.target.slice(0, 10)}.pdf`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 10_000);
		return blob;
	}

	return {
		generate: mutation.mutateAsync,
		generateAndDownload,
		isPending: mutation.isPending,
		isError: mutation.isError,
		error: mutation.error,
		downloadUrl,
		reset: () => {
			setDownloadUrl((prev) => {
				if (prev) URL.revokeObjectURL(prev);
				return null;
			});
			mutation.reset();
		},
	};
}
